import type {
  CreateLobbyRequest,
  CreateLobbyResponse,
  InProgressMatchItem,
  JoinLobbyByCodeRequest,
  JoinLobbyByCodeResponse,
  LobbyListItem,
  MatchHistoryFilter,
  MatchHistoryResponse,
  MatchRoomResponse,
  MyLobbyItem,
} from "@fortress/shared";
import { httpBase } from "../net/origin";
import { UnauthorizedError } from "./api";
import { withAuthRetry } from "./session";
import { getAppState } from "../appState";

/**
 * Authenticated fetch for the lobby endpoints: bearer JWT only. A 401 becomes
 * `UnauthorizedError`, which `withAuthRetry` turns into one refresh + retry —
 * the refresh token itself never travels on these requests. With AUTH_DISABLED
 * there is no auth header at all.
 */
async function authedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const { authDisabled } = await getAppState();
  const base = { "Content-Type": "application/json" };
  if (authDisabled) {
    return fetch(`${httpBase()}${path}`, { ...init, headers: base });
  }
  return withAuthRetry(async (accessToken) => {
    const res = await fetch(`${httpBase()}${path}`, {
      ...init,
      headers: { ...base, Authorization: `Bearer ${accessToken}` },
    });
    if (res.status === 401) throw new UnauthorizedError();
    return res;
  });
}

async function parseError(res: Response): Promise<string> {
  const detail = await res.json().catch(() => ({}));
  return (detail as { error?: string }).error ?? `HTTP ${res.status}`;
}

/** Create a lobby; client then enters `/match/:gameId`. */
export async function createLobby(
  body: CreateLobbyRequest,
): Promise<CreateLobbyResponse> {
  const res = await authedFetch("/lobbies", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<CreateLobbyResponse>;
}

/** Create a sandbox — a private, never-saved solo match that starts the
 * moment you join, for looking at the in-game UI (`POST /lobbies/sandbox`).
 * The client then enters `/match/:gameId` like any match. */
export async function createSandbox(): Promise<CreateLobbyResponse> {
  const res = await authedFetch("/lobbies/sandbox", { method: "POST" });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<CreateLobbyResponse>;
}

/** Create the tutorial — a private, never-saved match against a scripted
 * rival that walks the player through the rules (`POST /lobbies/tutorial`).
 * The client then enters `/match/:gameId` like any match. */
export async function createTutorial(): Promise<CreateLobbyResponse> {
  const res = await authedFetch("/lobbies/tutorial", { method: "POST" });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<CreateLobbyResponse>;
}

/** Public lobby browser rows. */
export async function listLobbies(): Promise<LobbyListItem[]> {
  const res = await authedFetch("/lobbies");
  if (!res.ok) throw new Error(await parseError(res));
  const body = (await res.json()) as { lobbies?: LobbyListItem[] };
  return body.lobbies ?? [];
}

/** This account's in-progress matches — entering `/match/:gameId` gets back
 * into any of them (the room endpoint revives dormant ones). */
export async function listMyMatches(): Promise<InProgressMatchItem[]> {
  const res = await authedFetch("/games/mine");
  if (!res.ok) throw new Error(await parseError(res));
  const body = (await res.json()) as { matches?: InProgressMatchItem[] };
  return body.matches ?? [];
}

/** One page of this account's finished matches, newest first. */
export async function listMatchHistory(opts: {
  status: MatchHistoryFilter;
  page: number;
}): Promise<MatchHistoryResponse> {
  const res = await authedFetch(
    `/games/history?status=${opts.status}&page=${opts.page}`,
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MatchHistoryResponse>;
}

/** The live Colyseus room hosting a match, by its gameId — the one lookup
 * behind `/match/:gameId`. Idempotently revives a dormant match (waiting
 * lobby, paused save, or finished game opened read-only) when no live room
 * exists; the returned roomId is fed straight to `joinById` and appears
 * nowhere else. */
export async function matchRoom(gameId: string): Promise<MatchRoomResponse> {
  const res = await authedFetch(`/games/${encodeURIComponent(gameId)}/room`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MatchRoomResponse>;
}

/** Mark a paused saved match as canceled; its document remains persisted. */
export async function cancelMatch(gameId: string): Promise<void> {
  const res = await authedFetch(`/games/${encodeURIComponent(gameId)}/cancel`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await parseError(res));
}

/** Resolve an invite code to the match it belongs to. */
export async function joinLobbyByCode(
  body: JoinLobbyByCodeRequest,
): Promise<JoinLobbyByCodeResponse> {
  const res = await authedFetch("/lobbies/join", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<JoinLobbyByCodeResponse>;
}

/** This account's open (waiting) lobbies — live or dormant. */
export async function listMyLobbies(): Promise<MyLobbyItem[]> {
  const res = await authedFetch("/lobbies/mine");
  if (!res.ok) throw new Error(await parseError(res));
  const body = (await res.json()) as { lobbies?: MyLobbyItem[] };
  return body.lobbies ?? [];
}

/** Close a waiting lobby for good (creator only; works live or dormant). */
export async function closeLobby(gameId: string): Promise<void> {
  const res = await authedFetch(`/lobbies/${encodeURIComponent(gameId)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await parseError(res));
}
